import { ShiftManagementSelectableEmployeesDocument } from 'generated/resources/graphql'

import { useMemo, useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useQuery } from 'modules/core/hooks/useQuery'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import type EmployeeModel from 'modules/employees/store/models/Employee'
import { useSelectableEmployeesByCurrent } from 'modules/shift_management/hooks/useSelectableEmployeesByCurrent'

const useSelectableEmployees = (
  startAt: string,
  endAt: string,
  contractVersions: ContractVersions
) => {
  const selectableEmployeesByCurrent = useSelectableEmployeesByCurrent(contractVersions)
  const memoizedSelectableEmployees = useMemo(() => selectableEmployeesByCurrent, [startAt, endAt])

  // Cache-first to avoid fetching the same data multiple times
  // because SelectableEmployees is stable and doesn't change frequently.
  const { data, loading } = useQuery(ShiftManagementSelectableEmployeesDocument, {
    fetchPolicy: 'cache-first',
    variables: {
      startAt,
      endAt,
    },
  })

  const selectableEmployees: EmployeeModel[] = useUnsafeMemo(() => {
    const employeesPermissions = data?.shiftManagement.employeesPermissions
    return memoizedSelectableEmployees.filter((employeeModel: EmployeeModel) => {
      const employee = employeesPermissions?.find((emp) => emp.id === employeeModel.id)

      if (employee) return employeeModel
    })
  }, [data?.shiftManagement.employeesPermissions, memoizedSelectableEmployees])

  return { loading, selectableEmployees }
}

export { useSelectableEmployees }
